import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { Grid } from '@mui/material'
import { Button, CardPropiedad } from '@toctoc/design-system'

import { selectPropertiesResult } from 'src/app/reducers/properties';
import { selectFavoritesState } from 'src/app/reducers/favorites';
import { useAppSelector } from 'src/app/hooks';
import { BannerMedio } from '@/components/particles/bannerMedio/BannerMedio';
import { IBannermedio } from '@/components/templates/search/interfaces/IEstructuraPrincipal';
import { IPropertyCard } from '@/components/templates/search/interfaces';
import { addImgSuffix, browserSupportsWebp, preferWebpExtension } from '@/utils/url';
import { eOriginId, ePropertyCondition } from '@/app/globalContext/globalContext.interfaces';
import { FavoriteButton } from '@/components/atoms/favoriteButton/FavoriteButton';

const cloudfrontUrl = process.env.NEXT_PUBLIC_CLOUDFRONT_IMG;
const BANNER_POSITION = 7;

// En mobile el corazón va a la izquierda; en desktop se mantiene a la derecha.
const PositionedFavoriteButton = styled(FavoriteButton)`
	position: absolute;
	top: 8px;
	right: 8px;
	z-index: 2;

	@media (max-width: 767px) {
		right: auto;
		left: 8px;
	}
`

export const ListadoPropiedades = ({ cardWide, columns, medio, listaSeoUrl, sessionInfo, guestSignUpModalHandler, selectedPropertyDataHandler, generateLead, favoriteClickHandler, quoteAndContactEnabled, onPropertyHover, highlightedPropertyId, onVerMas }: IBannermedio & { onPropertyHover?: (id: number | null) => void; highlightedPropertyId?: number | null; onVerMas?: (property: IPropertyCard) => void; favoriteClickHandler?: (property: IPropertyCard) => void }) => {
	const { isLogin, isGuest, userData } = sessionInfo ?? {};
	const results = useAppSelector(selectPropertiesResult);
	const favoritesState = useAppSelector(selectFavoritesState);
	const useWebpOnCards = useMemo(() => browserSupportsWebp(), []);
	const listingImageSrc = (rawSrc: string, withWatermarkSuffix: boolean): string => {
		const base = withWatermarkSuffix ? addImgSuffix(rawSrc, 'n_wm_') : rawSrc
		return useWebpOnCards ? preferWebpExtension(base) : base
	}
	const resultsWithMedia: IPropertyCard[] = results !== undefined && results !== null ? [...results] : [];
	// This is like cheating, but, you know
	if (resultsWithMedia.length > BANNER_POSITION && medio !== undefined && medio !== null)
		resultsWithMedia.splice(BANNER_POSITION, 0, { urlFicha: 'media'} as IPropertyCard)

	const hasUserData = isLogin || isGuest;
	const isListaSeoItau = listaSeoUrl?.indexOf('itau') !== -1;

	const leadHandler = (selectedProperty: IPropertyCard) => {
		selectedPropertyDataHandler(selectedProperty);

		if (hasUserData) {
			generateLead({
				propertyId: selectedProperty.idProperty,
				hashId: selectedProperty.hashId,
				originId: isListaSeoItau ? eOriginId.listaSeoItau : (selectedProperty.idEstadoPropiedad === ePropertyCondition.new ? eOriginId.listaSeoQuote : eOriginId.listaSeoContact),
				email: userData.email,
				utmSource: listaSeoUrl,
				isNewProperty: selectedProperty.idEstadoPropiedad === ePropertyCondition.new
			});
			guestSignUpModalHandler(true);
		} else {
			guestSignUpModalHandler(true);
		}
	}
	
	return (
		<Grid container rowSpacing={{ xs: 3, md: 3 }} columnSpacing={{ md: 2 }}>
			{resultsWithMedia.length > 0 && resultsWithMedia.map((info, index) => {
				const isBeneficioCliente = info.beneficioCliente !== undefined && info.beneficioCliente !== null;
				const showMoreButton = <>
					{quoteAndContactEnabled && <Button
						fullWidth
						variant='contained'
						style={{
							backgroundColor: isListaSeoItau ? "#FF6200" : "#3200C1",
						}}
						onClick={() => leadHandler(info)}
					>{info.idEstadoPropiedad === ePropertyCondition.new ? 'Cotizar' : 'Contactar'}</Button>}
					<Button
						fullWidth
						variant='outlined'
						style={{
							color: isListaSeoItau ? "#343A40" : "#3200C1",
							borderColor: isListaSeoItau ? "#343A40" : "#3200C1",
						}}
						{...(onVerMas
							? { onClick: () => onVerMas(info) }
							: { href: isBeneficioCliente && isListaSeoItau ? `${info.urlFichaItau}?utm_source=${listaSeoUrl}` : `${info.urlFicha}?utm_source=${listaSeoUrl}`, target: '_blank' }
						)}
					>Ver más</Button>
				</>
				// Defensa: si Seeker devuelve urlFicha con un shape que no es string, no debe romper el render.
				const partesURL = typeof info.urlFicha === 'string' ? info.urlFicha.split('/') : [];
				const ultimoParametro = partesURL[partesURL.length - 1];
				const columnsPerRow = columns ?? 3
				const sizeCard = cardWide ? 12 : Math.floor(12 / columnsPerRow)
				const isFavorite = favoritesState[info.idProperty] ?? info.esFavorita ?? false;
				return <Grid
					item xs={12} md={sizeCard} key={info.urlFicha}
					sx={{ zIndex: '1' }}
					onMouseEnter={() => onPropertyHover?.(info.idProperty)}
					onMouseLeave={() => onPropertyHover?.(null)}
				>
					{medio !== undefined && index === BANNER_POSITION ? <BannerMedio medio={medio} cardWide={cardWide} /> :
						<div style={{ position: 'relative', borderRadius: '8px', border: info.idProperty === highlightedPropertyId ? '2px solid #3200C1' : '2px solid transparent', transition: 'border-color 0.15s' }}>
							{favoriteClickHandler && (
								<PositionedFavoriteButton
									isFavorite={isFavorite}
									onClick={(e) => { e.stopPropagation(); favoriteClickHandler(info) }}
									variant="overlay"
								/>
							)}
							<CardPropiedad
								key={index}
								titulo={info.titulo}
								comuna={info.comuna}
								imagenPrincipal={{ src: listingImageSrc(info.imagenPrincipal.src, true), alt: info.imagenPrincipal.alt ?? info.titulo }}
								imagenInmobiliaria={{ src: listingImageSrc(info.imagenInmobiliaria.src, false), alt: info.imagenInmobiliaria.alt }}
								urlFicha={isBeneficioCliente ? `${info.urlFicha}?utm_source=${listaSeoUrl}` : `${info.urlFicha}`}
								esDestacada={info.esDestacada}
								esFavorita={isFavorite}
								esOferta={info.esOferta}
								badge={info.badge}
								tipoPropiedad={info.tipoPropiedad}
								tipoOperacion={info.tipoOperacion}
								precios={info.precios}
								superficie={info.superficie}
								dormitorios={info.dormitorios}
								bannos={info.bannos}
								oferta={info.oferta}
								horasPublicado={info.horasPublicado}
								cardWide={cardWide}
								cloudfrontUrl={cloudfrontUrl}
								data-apartado={info.tipoOperacion + ' ' + info.tipoPropiedad}
								data-elemento={ultimoParametro}
								customContainer={showMoreButton}
								beneficioCliente={info.beneficioCliente}
								disableUrlFicha
							/>
						</div>
					}

				</Grid>
			})}
		</Grid>
	)
}